import { Pause, Play } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import { cn } from "@/lib/utils"

interface AudioPlayerProps {
  audioUrl: string
  variant?: "light" | "dark"
  className?: string
}

export default function AudioPlayer({
  audioUrl,
  variant = "dark",
  className,
}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [error, setError] = useState(false)
  const [isSeeking, setIsSeeking] = useState(false)

  const isLight = variant === "light"

  // Handle play/pause — pause all other players first
  const togglePlay = () => {
    if (!audioRef.current) return

    if (isPlaying) {
      audioRef.current.pause()
    } else {
      window.dispatchEvent(new CustomEvent("audio-player:pause-all"))
      audioRef.current.play()
    }
  }

  // Listen for pause-all from other players
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handlePauseAll = () => {
      if (!audio.paused) audio.pause()
    }

    window.addEventListener("audio-player:pause-all", handlePauseAll)
    return () => window.removeEventListener("audio-player:pause-all", handlePauseAll)
  }, [])

  // Update time and handle audio events
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    // Only update time if not currently seeking to avoid jumping
    const updateTime = () => {
      if (!isSeeking) {
        setCurrentTime(audio.currentTime)
      }
    }
    const updateDuration = () => setDuration(audio.duration)
    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)
    const handleEnded = () => setIsPlaying(false)
    const handleError = (e: Event) => {
      console.error("Audio player error:", {
        url: audioUrl,
        error: e,
        audioError: audio.error,
        errorCode: audio.error?.code,
        errorMessage: audio.error?.message,
      })
      setError(true)
    }

    audio.addEventListener("timeupdate", updateTime)
    audio.addEventListener("loadedmetadata", updateDuration)
    audio.addEventListener("play", handlePlay)
    audio.addEventListener("pause", handlePause)
    audio.addEventListener("ended", handleEnded)
    audio.addEventListener("error", handleError)

    return () => {
      audio.removeEventListener("timeupdate", updateTime)
      audio.removeEventListener("loadedmetadata", updateDuration)
      audio.removeEventListener("play", handlePlay)
      audio.removeEventListener("pause", handlePause)
      audio.removeEventListener("ended", handleEnded)
      audio.removeEventListener("error", handleError)
    }
  }, [audioUrl, isSeeking])

  // Handle slider interaction (seeking)
  const handleSliderChange = (values: number[]) => {
    setIsSeeking(true)
    const newTime = (values[0] / 100) * duration
    setCurrentTime(newTime)
  }

  const handleSliderCommit = (values: number[]) => {
    if (!audioRef.current || !duration) return
    const newTime = (values[0] / 100) * duration
    audioRef.current.currentTime = newTime
    setIsSeeking(false)
  }

  // Handle container click (anywhere except button and slider)
  const handleContainerClick = (e: React.MouseEvent) => {
    // Prevent if clicking on interactive elements
    const target = e.target as HTMLElement
    if (
      target.closest("button") ||
      target.closest('[role="slider"]') ||
      target.closest('[data-slot="slider"]')
    ) {
      return
    }
    togglePlay()
  }

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds)) return "0:00"
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }

  // Show error state with helpful message
  if (error) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800 dark:bg-red-900/20",
          className,
        )}
      >
        <span className="text-xs text-red-600 dark:text-red-400">
          Audio unavailable
        </span>
      </div>
    )
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div
      onClick={handleContainerClick}
      data-testid="audio-player-container"
      dir="ltr"
      className={cn(
        "group flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors",
        isLight
          ? "hover:bg-white/10 active:bg-white/20"
          : "hover:bg-accent/50 active:bg-accent",
        className,
      )}
    >
      {/* Hidden native audio element */}
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      {/* Play/Pause button */}
      <Button
        type="button"
        size="icon-sm"
        variant={isLight ? "ghost" : "outline"}
        onClick={(e) => {
          e.stopPropagation()
          togglePlay()
        }}
        className={cn(
          "shrink-0",
          isLight && "border-white/30 text-white hover:bg-white/20",
        )}
        aria-label={isPlaying ? "Pause audio" : "Play audio"}
        data-testid="audio-play-button"
      >
        {isPlaying ? (
          <Pause className="size-3.5" />
        ) : (
          <Play className="size-3.5" />
        )}
      </Button>

      {/* Timestamp */}
      <span
        className={cn(
          "shrink-0 text-xs font-medium tabular-nums",
          isLight ? "text-white/90" : "text-muted-foreground",
        )}
        data-testid="audio-timestamp"
      >
        {formatTime(currentTime)}
      </span>

      {/* Progress slider */}
      <Slider
        value={[progress]}
        max={100}
        step={0.1}
        onValueChange={handleSliderChange}
        onValueCommit={handleSliderCommit}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "flex-1 cursor-pointer",
          isLight &&
            "[&_[data-slot=slider-range]]:bg-white [&_[data-slot=slider-thumb]]:border-white [&_[data-slot=slider-thumb]]:bg-white [&_[data-slot=slider-track]]:bg-white/20",
        )}
        data-testid="audio-slider"
        aria-label="Seek audio position"
      />
    </div>
  )
}
